import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { PuzzleInfo } from '@draughts/shared';
import { fetchDailyPuzzle } from '../../services/puzzle-api';
import { variantPill } from '../../utils/variant-styling';
import PuzzlePreviewBoard from '../shared/PuzzlePreviewBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import PuzzleUnavailable from './PuzzleUnavailable';
import './DailyPuzzleInvitePage.css';

interface DailyPuzzleInvitePageProps {
  /** Lowercased variant carried by the shared link. */
  variant: string;
  /**
   * App has not settled auth yet. Shows a neutral spinner instead of the pitch:
   * a REGISTERED recipient crosses this screen for an instant before the
   * resolver navigates them to the board, and must never see a flash of
   * "you need to sign up".
   */
  checking?: boolean;
  onCreateAccount: () => void;
  onLogin: () => void;
  /** Offer the (free, client-side) demo puzzle — omitted, the CTA is hidden. */
  onTryDemo?: () => void;
  onLeave: () => void;
}

type Phase = 'loading' | 'ready' | 'unavailable';

/**
 * The screen a visitor lands on from a shared daily-puzzle link
 * (`/?puzzle=daily&variant=…`) when they are not a registered user.
 *
 * The daily puzzle is registered-only — it carries a rating and a Streak, and
 * guests are given the client-side demo puzzle instead. This screen used not to
 * exist: the link silently minted a guest session and handed over the real
 * puzzle, which was the one hole in that rule.
 *
 * It still has to *sell* the puzzle rather than just refuse it, so it shows the
 * real position, blurred and inert, plus its rating and length. All of that
 * comes from GET /puzzles/daily, which is public and returns no solution.
 */
function DailyPuzzleInvitePage({
  variant,
  checking = false,
  onCreateAccount,
  onLogin,
  onTryDemo,
  onLeave,
}: DailyPuzzleInvitePageProps) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState<PuzzleInfo | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');

  // Public endpoint, so this works logged out.
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    void fetchDailyPuzzle(variant).then((res) => {
      if (cancelled) return;
      if (!res?.puzzle) {
        // fetchDailyPuzzle collapses 404 and 500 into null — either way there is
        // nothing to preview today.
        setPhase('unavailable');
        return;
      }
      setPuzzle(res.puzzle);
      setPhase('ready');
    });
    return () => { cancelled = true; };
  }, [variant]);

  if (checking || phase === 'loading') {
    return (
      <div className="pzi-page">
        <div className="pzi-card">
          <div className="pzi-spinner" />
          <p className="pzi-body">{t('puzzle.loading', 'Loading puzzle...')}</p>
        </div>
      </div>
    );
  }

  if (phase === 'unavailable' || !puzzle) {
    return (
      <div className="pzi-page">
        <div className="pzi-card">
          <PuzzleUnavailable reason="link" onRegister={onCreateAccount} onHome={onLeave} />
        </div>
      </div>
    );
  }

  const pill = variantPill(puzzle.variant);
  const side = puzzle.playerColor === 'black' ? t('puzzle.black', 'Black') : t('puzzle.white', 'White');

  return (
    <div className="pzi-page">
      <div className="pzi-card">
        {/* "Daily puzzle" is a brand term — kept in English across all locales.
            See packages/web/src/__tests__/i18n-brand-terms.test.ts. */}
        <div className="pzi-eyebrow">Daily puzzle</div>

        {/* Decorative: it is blurred, so the real information is in the text
            below. Keeping it out of the accessibility tree avoids announcing a
            board a screen-reader user cannot act on. */}
        <div className="pzi-board-wrap" aria-hidden="true">
          {/* La cornice sta DENTRO il wrap: il wrap resta l'ancora del lucchetto,
              che gli sta sopra in assoluto. E la sfocatura resta sulla sola
              damiera — una cornice sfocata non e' una cornice. */}
          <CorniceDamiera className="pzi-cornice">
            <PuzzlePreviewBoard
              fen={puzzle.fen}
              variant={puzzle.variant.toLowerCase()}
              flipped={puzzle.playerColor === 'black'}
              className="pzi-board-svg"
              themed
            />
          </CorniceDamiera>
          <span className="pzi-lock">
            <Lock size={22} aria-hidden="true" />
          </span>
        </div>
        <p className="pzi-locked-caption">{t('puzzle.invite.locked', 'Preview locked')}</p>

        <div className="pzi-tags">
          <span className="pzi-tag">{side} {t('puzzle.toPlayAndWin', 'to play and win')}</span>
          {puzzle.rating != null && <span className="pzi-tag muted">Lv. {puzzle.rating}</span>}
          {puzzle.moveCount != null && (
            <span className="pzi-tag muted">
              {t('puzzle.invite.moves', { count: puzzle.moveCount, defaultValue: '{{count}} moves' })}
            </span>
          )}
          <span className="pzi-tag muted">{pill.flag} {pill.code}</span>
        </div>

        <div className="pzi-restriction">
          {t('auth.guestRestriction', 'This feature requires a registered account')}
        </div>
        <h2 className="pzi-title">
          {t('puzzle.invite.title', "Today's daily puzzle is waiting for you")}
        </h2>
        <p className="pzi-body">
          {t(
            'puzzle.invite.body',
            'A friend shared today’s daily puzzle with you. Daily puzzles are reserved for registered players — create a free account, or log in, to unlock the board.',
          )}
        </p>

        <div className="pzi-actions">
          <button type="button" className="pzi-cta pzi-primary" onClick={onCreateAccount}>
            {t('puzzle.demo.registerCta', 'Create a free account')}
          </button>
          <button type="button" className="pzi-cta pzi-secondary" onClick={onLogin}>
            {t('auth.signIn', 'Sign In')}
          </button>
          {onTryDemo && (
            <button type="button" className="pzi-cta pzi-ghost" onClick={onTryDemo}>
              {t('puzzle.invite.tryDemo', 'Try the demo puzzle meanwhile')}
            </button>
          )}
          <button type="button" className="pzi-cta pzi-ghost" onClick={onLeave}>
            {t('puzzle.unavailable.home', 'Go to the home page')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DailyPuzzleInvitePage;
